'use client';

import dynamic from 'next/dynamic';
import classnames from 'classnames/bind';
import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { resolveUrl } from '@portal-root/geo-utils';
import { openDrawerHandler } from '@portals/help-center-core';
import { Text } from '@portals/ui-kit';
import { EColors } from '@/_types';
import {
  EBlockName,
  EContentText,
  TCard,
  TContentList,
  TContentText,
  TOpenDrawerParams,
} from '../../_types';
import { Tool } from './_components/tool';
import { CardButton } from './_components/card-button';
import { Slider } from './_components/slider';
import { CardObserver } from './_components/card-observer';
import styles from './index.module.scss';

const cn = classnames.bind(styles);
const BLOCK_NAME = 'Card';

const JamModal = dynamic(
  () =>
    import('./_components/jam-modal').then((module) => ({
      default: module.JamModal,
    })),
  {
    ssr: false,
  },
);

export const Card = ({
  title,
  description,
  tool,
  button,
  variant,
  slides,
  sliderId,
  blockName,
  trialPromoOffer,
  modal,
  drawer,
}: TCard) => {
  const translations = useTranslations();
  const locale = useLocale();

  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  const [isModalOpened, setIsModalOpened] = useState(false);

  const handleModalOpen = useCallback(() => {
    setIsModalOpened(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpened(false);
  }, []);

  const handleLinkClick = useCallback(() => {
    window.open(resolveUrl(button?.link), '_blank');
  }, [button?.link]);

  const handleDrawerOpen = (params: TOpenDrawerParams) => {
    openDrawerHandler(params);
  };

  const onClick = () => {
    switch (blockName) {
      case EBlockName.JAM:
        if (drawer) {
          return handleDrawerOpen(drawer);
        }

        if (modal) {
          return handleModalOpen();
        }

        if (button?.link) {
          return handleLinkClick();
        }

        break;
      default:
        return handleLinkClick();
    }
  };

  const getDescriptionText = (
    block: TContentText | TContentList,
    blockIndex: number,
  ) => {
    switch (block.type) {
      case EContentText.LIST:
        return (
          <ul key={blockIndex} className={cn(`${BLOCK_NAME}__list`)}>
            {block.items.map((item, index) => (
              <li key={index}>
                <Text size="body-l" text={translations(item)} />
              </li>
            ))}
          </ul>
        );
      case EContentText.TEXT:
        return (
          <Text
            key={blockIndex}
            size="body-l"
            text={translations(block.text)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={cn(BLOCK_NAME, {
        [`${BLOCK_NAME}--with-button`]: button?.title,
      })}
    >
      <div className={cn(`${BLOCK_NAME}__info`)}>
        <div className={cn(`${BLOCK_NAME}__title-wrapper`)}>
          {tool && <Tool {...tool} />}
          <Text size="headline-s" text={translations(title)} />
        </div>
        <div className={cn(`${BLOCK_NAME}__description`)}>
          {description.map((block, index) => getDescriptionText(block, index))}
        </div>
      </div>
      <div
        className={cn(
          `${BLOCK_NAME}__slider`,
          `${BLOCK_NAME}__slider--${locale}`,
        )}
      >
        <Slider
          activeSlideIndex={activeSlideIndex}
          blockName={blockName}
          setActiveSlideIndex={setActiveSlideIndex}
          sliderId={sliderId}
          slides={slides}
          variant={variant}
        />
      </div>
      {(button || trialPromoOffer) && (
        <div className={cn(`${BLOCK_NAME}__button-wrapper`)}>
          {trialPromoOffer && (
            <Text
              color={EColors.ORANGE}
              size="body-l"
              text={translations(trialPromoOffer)}
            />
          )}

          {button && (
            <CardButton
              blockName={blockName}
              buttonProps={button}
              onClick={onClick}
            />
          )}
        </div>
      )}
      <div className={cn(`${BLOCK_NAME}__observer`)}>
        <CardObserver blockName={blockName} />
      </div>
      {modal && (
        <JamModal
          {...modal}
          isOpened={isModalOpened}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};
